import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  ContactoAlta, DatosAlta, DomicilioFiscal, fiscalSinVacios, PIEZAS_FISCAL, PapelContacto, PAPELES, SolicitudesAltaService, telefonoInvalido, AYUDA_TELEFONO,
} from 'src/app/services/workflows/solicitudes-alta.service';
import { mensajeDeError } from 'src/app/services/workflows/workflows.service';
import { PasoRiel } from '../riel-pasos/riel-pasos.component';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import {
  acronimoConFormato, dominioPortal, limpiarSiglas, siglasDe, siglasValidas,
} from 'src/app/utils/url-propia';

/** Las tres paradas de la solicitud nueva (CONTRATO-REDISENO-ALTAS.md, D). Ese orden, siempre. */
type PasoNueva = 'comercio' | 'contacto' | 'archivo';
const ORDEN_PASOS: PasoNueva[] = ['comercio', 'contacto', 'archivo'];

/** El renglón de contacto en edición: nunca se manda vacío del todo, tal cual. */
interface ContactoFila { papel: PapelContacto; nombre: string; correo: string; telefono: string; }

/** Como lo limpia el núcleo (`normalizarRif`): mayúsculas y sin guiones, puntos ni espacios. Así entra
 *  el RIF tal como lo dicta el dueño: «j40123456 7», «J-40.123.456-7». El servidor vuelve a validar. */
const limpiarRif = (rif: string): string => String(rif || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const RIF_LIMPIO = /^[VEJPGC]\d{8,9}$/;

/** Lo escrito en la nueva, para que no se pierda si el ejecutivo sale a otra opción o recarga. */
const BORRADOR = 'alta-nueva-borrador';

/**
 * Nueva solicitud de alta de comercio — CONTRATO-ALTAS.md, constructor C. La carga el ejecutivo
 * comercial: queda a su nombre, en la etapa 1. Molde: workflows-lista / workflow-detalle.
 */
@Component({
  selector: 'app-alta-nueva',
  templateUrl: './alta-nueva.component.html',
  styleUrls: ['./alta-nueva.component.css'],
})
export class AltaNuevaComponent implements OnInit, OnDestroy {
  PAPELES = PAPELES;
  PIEZAS_FISCAL = PIEZAS_FISCAL;

  form = {
    nombre_comercial: '', razon_social: '', rif: '',
    fiscal: { street: '', city: '', municipality: '', state: '', postal_code: '' } as Required<DomicilioFiscal>,
    tipo_persona: '', telefono: '', correo: '', descripcion: '',
    siglas: '', acronimo: '',
  };

  // ── URL propia: siglas del código y acrónimo de la dirección del portal ──────────────────────
  /** Dejan de re-sugerirse apenas el usuario las toca. */
  siglasTocadas = false;
  acronimoTocado = false;
  /** El sufijo del campo («.mc.creditotal.online»). */
  sufijoDominio = `.${dominioPortal()}`;
  /** Lo que dijo «disponible» del acrónimo escrito: `revisando` mientras se espera, `ok`, o el motivo. */
  acronimoEstado: { fase: 'vacio' | 'revisando' | 'ok' | 'mal'; mensaje: string; sugerido: boolean } =
    { fase: 'vacio', mensaje: '', sugerido: false };
  private acronimoTimer: any = null;
  private acronimoPedido = 0;

  get siglasOk(): boolean { return siglasValidas(this.form.siglas); }

  alCambiarNombre(): void {
    if (!this.siglasTocadas) this.form.siglas = siglasDe(this.form.nombre_comercial);
    if (!this.acronimoTocado) this.sugerirAcronimo();
  }

  alEscribirSiglas(): void {
    this.siglasTocadas = true;
    this.form.siglas = limpiarSiglas(this.form.siglas);
  }

  alEscribirAcronimo(): void {
    this.acronimoTocado = true;
    this.form.acronimo = String(this.form.acronimo || '').toLowerCase().trim();
    this.revisarAcronimo(false);
  }

  /** El acrónimo sugerido del nombre: lo calcula el núcleo y ya viene libre. Con debounce. */
  private sugerirAcronimo(): void {
    clearTimeout(this.acronimoTimer);
    const pedido = ++this.acronimoPedido;
    const nombre = this.form.nombre_comercial.trim();
    if (!nombre) { this.form.acronimo = ''; this.acronimoEstado = { fase: 'vacio', mensaje: '', sugerido: false }; return; }
    this.acronimoTimer = setTimeout(async () => {
      try {
        const r = await this._merchant.sugerirAcronimo(nombre);
        if (pedido !== this.acronimoPedido || this.acronimoTocado) return;
        this.form.acronimo = r?.sugerido || '';
        if (this.form.acronimo) this.revisarAcronimo(true); else this.acronimoEstado = { fase: 'vacio', mensaje: '', sugerido: false };
      } catch (e) { /* sin sugerencia: lo escribe la persona */ }
    }, 400);
  }

  /** Valida en vivo: el formato se mira acá; reservado y ocupado, el núcleo (con debounce). */
  revisarAcronimo(sugerido: boolean): void {
    clearTimeout(this.acronimoTimer);
    const pedido = ++this.acronimoPedido;
    const v = this.form.acronimo;
    if (!v) { this.acronimoEstado = { fase: 'vacio', mensaje: '', sugerido: false }; return; }
    if (!acronimoConFormato(v)) { this.acronimoEstado = this.malAcronimo('formato', v); return; }
    this.acronimoEstado = { fase: 'revisando', mensaje: 'Revisando si está libre…', sugerido: false };
    this.acronimoTimer = setTimeout(async () => {
      try {
        const r = await this._merchant.acronimoDisponible(v);
        if (pedido !== this.acronimoPedido) return;
        this.acronimoEstado = r.libre
          ? { fase: 'ok', mensaje: '', sugerido: sugerido || !this.acronimoTocado }
          : this.malAcronimo(r.motivo || 'formato', v);
      } catch (e) {
        if (pedido !== this.acronimoPedido) return;
        this.acronimoEstado = { fase: 'mal', mensaje: 'No se pudo revisar si está libre. Vuelve a intentarlo.', sugerido: false };
      }
    }, 350);
  }

  private malAcronimo(motivo: string, v: string): { fase: 'mal'; mensaje: string; sugerido: boolean } {
    const m = motivo === 'reservado' ? 'Esa palabra está reservada por el sistema. Elige otra.'
      : motivo === 'ocupado' ? `Ya la usa otro comercio. Prueba con ${v}-2.`
        : 'Sólo minúsculas, números y guiones; no puede empezar ni terminar con guion.';
    return { fase: 'mal', mensaje: m, sugerido: false };
  }

  get direccionDelPortal(): string { return `https://${this.form.acronimo}${this.sufijoDominio}`; }

  // Arranca con un renglón de administrador vacío (contrato).
  contactos: ContactoFila[] = [this.filaVacia('ADMINISTRADOR')];

  enviando = false;
  error = '';
  detalle: string[] = [];

  // El paso a la vista. No se guarda en el borrador (contrato: se guarda lo escrito, no dónde
  // estaba parado); siempre arranca en el primero.
  pasoActual: PasoNueva = 'comercio';

  // El RIF en archivo (contrato §4): obligatorio para crear, y el borrador NO lo puede guardar
  // (no sobrevive a `JSON.stringify` en sessionStorage): hay que volver a elegirlo tras recargar.
  rifArchivo: File | null = null;
  errorRif = '';

  elegirArchivoRif(event: Event): void {
    const input = event.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    input.value = '';   // permite volver a elegir el mismo archivo si hubo que corregir
    if (!archivo) return;
    if (!/^(application\/pdf|image\/jpeg|image\/png)$/.test(archivo.type)) {
      this.errorRif = 'Sube un PDF, JPG o PNG.'; this.rifArchivo = null; return;
    }
    if (archivo.size > 8 * 1024 * 1024) {
      this.errorRif = 'El archivo pesa más de 8 MB.'; this.rifArchivo = null; return;
    }
    this.errorRif = '';
    this.rifArchivo = archivo;
  }

  /** «245 KB» / «1.3 MB»: el peso tal como lo lee alguien, nunca en bytes crudos. */
  pesoArchivo(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // El RIF se marca inválido recién al salir del campo; una vez que hubo error, se sigue
  // validando en vivo mientras se escribe, para que se apague apenas quede bien.
  rifTocado = false;
  /** Al salir del campo: si quedó bien, se escribe como se lee (J-40123456-7). */
  marcarRifTocado(): void {
    this.rifTocado = true;
    if (this.form.rif.trim() && this.rifValido) this.form.rif = this.rifConGuiones;
  }

  private get rifConGuiones(): string {
    const r = limpiarRif(this.form.rif);
    return r.length === 10 ? `${r[0]}-${r.slice(1, 9)}-${r[9]}` : `${r[0]}-${r.slice(1)}`;
  }

  /** Qué está mal, dicho en concreto (no «falta algo»). */
  get rifError(): string {
    const r = limpiarRif(this.form.rif);
    if (!/^[VEJPGC]/.test(r)) return 'Empieza con la letra: V, E, J, P, G o C.';
    const digitos = r.slice(1);
    if (/\D/.test(digitos)) return 'Después de la letra van solo números.';
    return `Después de la letra van 8 o 9 dígitos, y hay ${digitos.length}.`;
  }
  get rifInvalido(): boolean { return this.rifTocado && !this.rifValido; }

  // Minuta del 5-oct, «longitud del teléfono»: el mismo criterio del núcleo.
  telefonoInvalido = telefonoInvalido;
  AYUDA_TELEFONO = AYUDA_TELEFONO;

  /** Se recuperó lo escrito la vez anterior (se dice arriba, con cómo empezar de cero). */
  recuperado = false;
  private creada = false;

  constructor(private _alta: SolicitudesAltaService, private router: Router, private _merchant: MerchantService) { }

  ngOnInit(): void {
    try {
      const b = JSON.parse(sessionStorage.getItem(BORRADOR) || 'null');
      if (b && b.form) {
        this.form = { ...this.form, ...b.form };
        this.siglasTocadas = !!b.siglasTocadas; this.acronimoTocado = !!b.acronimoTocado;
        if (this.form.acronimo) this.revisarAcronimo(false);
        if (Array.isArray(b.contactos) && b.contactos.length) this.contactos = b.contactos;
        this.recuperado = !this.vacio;
      }
    } catch (e) { /* sin almacenamiento: arranca vacía */ }
  }

  ngOnDestroy(): void { this.guardarBorrador(); }

  @HostListener('window:beforeunload')
  guardarBorrador(): void {
    try {
      if (this.creada || this.vacio) sessionStorage.removeItem(BORRADOR);
      else sessionStorage.setItem(BORRADOR, JSON.stringify({
        form: this.form, contactos: this.contactos, siglasTocadas: this.siglasTocadas, acronimoTocado: this.acronimoTocado,
      }));
    } catch (e) { /* sin almacenamiento: no se guarda */ }
  }

  empezarDeCero(): void {
    this.form = { nombre_comercial: '', razon_social: '', rif: '', fiscal: { street: '', city: '', municipality: '', state: '', postal_code: '' }, tipo_persona: '', telefono: '', correo: '', descripcion: '', siglas: '', acronimo: '' };
    this.siglasTocadas = false; this.acronimoTocado = false;
    this.acronimoEstado = { fase: 'vacio', mensaje: '', sugerido: false };
    this.contactos = [this.filaVacia('ADMINISTRADOR')];
    this.rifTocado = false; this.recuperado = false;
    this.rifArchivo = null; this.errorRif = '';
    this.pasoActual = 'comercio';
    try { sessionStorage.removeItem(BORRADOR); } catch (e) { /* nada */ }
  }

  // ── El riel de tres pasos (contrato D) ─────────────────────────────────

  private indice(p: PasoNueva): number { return ORDEN_PASOS.indexOf(p); }

  get pasosRiel(): PasoRiel[] {
    const nombres: Record<PasoNueva, string> = {
      comercio: 'El comercio', contacto: 'Contacto administrador', archivo: 'RIF en archivo',
    };
    return ORDEN_PASOS.map((clave) => {
      const i = this.indice(clave);
      const actual = this.indice(this.pasoActual);
      const estado: PasoRiel['estado'] = i === actual ? 'ACTUAL' : (i < actual ? 'HECHO' : 'PENDIENTE');
      return { clave, nombre: nombres[clave], estado };
    });
  }

  /** Solo deja saltar a un paso ya recorrido (HECHO) o al actual; el riel ya no emite los pendientes
   *  (quedan deshabilitados), esto es el segundo cierre por si acaso. */
  irAPaso(clave: string): void {
    const p = clave as PasoNueva;
    if (this.indice(p) <= this.indice(this.pasoActual)) this.pasoActual = p;
  }

  siguiente(): void {
    if (this.pasoActual === 'comercio' && this.comercioCompleto) this.pasoActual = 'contacto';
    else if (this.pasoActual === 'contacto' && this.contactoCompleto) this.pasoActual = 'archivo';
  }

  anterior(): void {
    if (this.pasoActual === 'archivo') this.pasoActual = 'contacto';
    else if (this.pasoActual === 'contacto') this.pasoActual = 'comercio';
  }

  get puedeAvanzar(): boolean {
    if (this.pasoActual === 'comercio') return this.comercioCompleto;
    if (this.pasoActual === 'contacto') return this.contactoCompleto;
    return false;
  }

  /** Lo que falta, pero solo del paso en pantalla (los mínimos del comercio ya se marcan con el
   *  asterisco rojo y la pista del RIF; acá no se repiten, igual que en la versión de antes). */
  get mensajeFaltaPaso(): string {
    if (this.pasoActual === 'comercio') return '';
    const falta = this.pasoActual === 'contacto' ? this.faltaContacto : this.faltaArchivo;
    if (!falta.length) return '';
    return this.pasoActual === 'archivo' ? `Para crear falta: ${falta.join(', ')}.` : `Para seguir falta: ${falta.join(', ')}.`;
  }

  private get vacio(): boolean {
    // Las siglas y el acrónimo sugeridos solos no cuentan como «escrito»: sólo si la persona los tocó.
    const { siglas, acronimo, ...resto } = this.form;
    const propios = (this.siglasTocadas && siglas.trim()) || (this.acronimoTocado && acronimo.trim());
    return !propios && !Object.values(resto).some((v) => String(v || '').trim()) && this.contactos.every((f) => this.filaVaciaDelTodo(f));
  }

  private filaVacia(papel: PapelContacto): ContactoFila {
    return { papel, nombre: '', correo: '', telefono: '' };
  }

  // ── Validación en pantalla (el servidor vuelve a validar todo) ───────────

  get rifValido(): boolean { return !this.form.rif.trim() || RIF_LIMPIO.test(limpiarRif(this.form.rif)); }

  get faltanMinimos(): boolean {
    const f = this.form;
    return !f.nombre_comercial.trim() || !f.razon_social.trim()
      || !f.fiscal.street.trim() || !f.fiscal.city.trim() || !f.rif.trim() || !this.rifValido
      || !f.descripcion.trim() || telefonoInvalido(f.telefono)
      || !this.siglasOk || this.acronimoEstado.fase !== 'ok';
  }

  filaVaciaDelTodo(f: ContactoFila): boolean {
    return !f.nombre.trim() && !f.correo.trim() && !f.telefono.trim();
  }

  /** A medias: le falta el nombre, o le falta el correo y el teléfono a la vez. */
  filaAMedias(f: ContactoFila): boolean {
    if (this.filaVaciaDelTodo(f)) return false;
    return !f.nombre.trim() || (!f.correo.trim() && !f.telefono.trim()) || telefonoInvalido(f.telefono);
  }

  get hayFilaAMedias(): boolean { return this.contactos.some((f) => this.filaAMedias(f)); }

  /** El contacto administrador es obligatorio para crear (contrato §1, `para_crear`): con nombre, y
   *  correo o teléfono. Se busca por papel, no por posición: el ejecutivo puede reordenar las filas. */
  get administradorValido(): boolean {
    return this.contactos.some((c) => c.papel === 'ADMINISTRADOR' && c.nombre.trim() && (c.correo.trim() || c.telefono.trim()));
  }

  // Completo por paso (contrato §4: los mínimos del comercio, el administrador, el RIF en archivo).
  get comercioCompleto(): boolean { return !this.faltanMinimos; }
  get contactoCompleto(): boolean { return !this.hayFilaAMedias && this.administradorValido; }
  get archivoCompleto(): boolean { return !!this.rifArchivo && !this.errorRif; }

  /** Lo que falta del paso «Contacto administrador», dicho en concreto. Si hay una fila a medias, ya
   *  lo dice ella misma junto al renglón: no se repite acá. */
  get faltaContacto(): string[] {
    if (this.hayFilaAMedias) return [];
    return this.administradorValido ? [] : ['el contacto administrador (nombre y correo o teléfono)'];
  }

  get faltaArchivo(): string[] { return this.rifArchivo ? [] : ['el RIF en archivo']; }

  get puedeCrear(): boolean {
    return !this.enviando && this.comercioCompleto && this.contactoCompleto && this.archivoCompleto;
  }

  agregarContacto(): void { this.contactos = [...this.contactos, this.filaVacia('OTRO')]; }
  quitarContacto(i: number): void { this.contactos = this.contactos.filter((_, k) => k !== i); }

  // ── Armar lo que se manda: solo los campos con valor ──────────────────────

  private datosAEnviar(): DatosAlta {
    const f = this.form;
    const d: DatosAlta = {};
    if (f.nombre_comercial.trim()) d.nombre_comercial = f.nombre_comercial.trim();
    if (f.razon_social.trim()) d.razon_social = f.razon_social.trim();
    if (f.rif.trim()) d.rif = this.rifConGuiones;
    const fiscal = fiscalSinVacios(f.fiscal);
    if (Object.keys(fiscal).length) d.fiscal = fiscal;
    if (f.tipo_persona) d.tipo_persona = f.tipo_persona;
    if (f.telefono.trim()) d.telefono = f.telefono.trim();
    if (f.correo.trim()) d.correo = f.correo.trim();
    if (f.descripcion.trim()) d.descripcion = f.descripcion.trim();
    if (f.siglas) d.siglas = f.siglas;
    if (f.acronimo.trim()) d.acronimo = f.acronimo.trim();
    return d;
  }

  private contactosAEnviar(): ContactoAlta[] {
    return this.contactos.filter((f) => !this.filaVaciaDelTodo(f)).map((f) => {
      // Se manda `papeles[]` (vocabulario 2026-09-29: un contacto puede tener varios papeles); acá
      // se sigue eligiendo uno solo por fila, pero ya en la forma nueva.
      const c: ContactoAlta = { papeles: [f.papel], nombre: f.nombre.trim() };
      if (f.correo.trim()) c.correo = f.correo.trim();
      if (f.telefono.trim()) c.telefono = f.telefono.trim();
      return c;
    });
  }

  crear = async (): Promise<void> => {
    if (!this.puedeCrear || !this.rifArchivo) return;
    this.enviando = true; this.error = ''; this.detalle = [];
    try {
      const s = await this._alta.crear(this.datosAEnviar(), this.contactosAEnviar(), this.rifArchivo);
      this.creada = true;
      try { sessionStorage.removeItem(BORRADOR); } catch (e) { /* nada */ }
      this.router.navigate(['/console/altas', s._id], {
        state: { aviso: `Se creó la solicitud ${s.numero}. Está a tu nombre, en la etapa 1.` },
      });
    } catch (e: any) {
      this.error = mensajeDeError(e, 'No se pudo crear la solicitud.');
      this.detalle = Array.isArray(e?.error?.detalle) ? e.error.detalle : [];
      // 403: el mensaje del servidor ya dice quién puede; solo se agrega dónde pedirlo.
      if (e?.status === 403) this.error = `${this.error} Pide el perfil en Roles y permisos.`;
    } finally {
      this.enviando = false;
    }
  };
}
